import { Box, CircularProgress, Pagination } from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import CInput from "../../../../components/CInput";
import CAutocomplete from "../../../../components/CAutocomplete";
import SnagDetailsModal from "./SnagDetailsModal";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import styles from "../../styles";
import { ReactComponent as LeftArrowIcon } from "../../../../assets/svg/LeftArrowIcon.svg";
import {
  inspectionStatusNewStyleArray,
  milestoneStatusStyleArray,
} from "../../config";
import moment from "moment";
import { getApiData } from "../../../../utils/APIHelper";
import { isArray, isEmpty } from "lodash";
import SnagList from "./SnagList";
import stylesModule from "./MilestoneSnagDetails.module.css";

export const getStatusDisplayText = (text) => {
  if (typeof text !== "string") return text;
  const displayText = text
    .replace(/[-_]/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return displayText;
};

function MilestoneSnagDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const { visitId } = useParams();

  const { milestoneDetails, styles: locationStyles } = location.state || {};
  // Use location styles if available, otherwise fall back to imported styles
  const componentStyles = locationStyles || styles(false); // false for desktop view

  // Fallback styles to ensure component looks good
  const fallbackStyles = {
    container: {
      fontFamily: "Inter Tight",
      minHeight: "100vh",
      backgroundColor: "#fff",
      ...componentStyles.container,
    },
    header: {
      padding: "4vh 2vw 2vh",
      background:
        "linear-gradient(180deg, rgba(255, 162, 162, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%)",
      ...componentStyles.header,
    },
    headerContent: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: "30px",
      ...componentStyles.headerContent,
    },
    idText: {
      fontSize: "12px",
      color: "#0D0D0E",
      ...componentStyles.idText,
    },
    subContainer: {
      padding: "2vh 2vw",
      ...componentStyles.subContainer,
    },
    rowFlex: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      ...componentStyles.rowFlex,
    },
    columnFlex: {
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      ...componentStyles.columnFlex,
    },
    projectHeading: {
      color: "#0D0D0E",
      fontSize: "22px",
      fontWeight: "500",
      margin: 0,
      ...componentStyles.projectHeading,
    },
    statusContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: "10px",
      ...componentStyles.statusContainer,
    },
    milestoneStatusCircle: {
      width: "15px",
      height: "15px",
      borderRadius: "50%",
      border: "3px solid rgb(229, 229, 229)",
      ...componentStyles.milestoneStatusCircle,
    },
    statusText: {
      fontSize: "14px",
      color: "#0D0D0E",
      ...componentStyles.statusText,
    },
    idLabel: {
      color: "#949DAA",
      fontSize: "14px",
      margin: 0,
      ...componentStyles.idLabel,
    },
    price: {
      color: "#0D0D0E",
      fontSize: "18px",
      fontWeight: "500",
      margin: 0,
      ...componentStyles.price,
    },
    overview_section: {
      display: "flex",
      flexDirection: "row",
      alignItems: "flex-start",
      marginTop: "2vh",
      gap: "50px",
      ...componentStyles.overview_section,
    },
    section_1: {
      flex: 2,
      ...componentStyles.section_1,
    },
    section_sub_heading: {
      color: "#949DAA",
      fontSize: "14px",
      fontWeight: "500",
      margin: 0,
      ...componentStyles.section_sub_heading,
    },
  };

  const [milestoneLoader, setMilestoneLoader] = useState(true);
  const [loader, setLoader] = useState(true);
  const [details, setDetails] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1); // 1-based
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedSnag, setSelectedSnag] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const debounceTimeout = useRef();

  useEffect(() => {
    if (search.length === 0) {
      setCategoryFilter("");
      setPage(1);
      return;
    }
    if (search.length >= 3) {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
      debounceTimeout.current = setTimeout(() => {
        setCategoryFilter(search);
        setPage(1);
      }, 500);
    }
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [categoryFilter, statusFilter]);

  useEffect(() => {
    if (milestoneDetails && visitId) {
      fetchSnagDetails();
    }
  }, [milestoneDetails, visitId, categoryFilter, statusFilter, page]);

  useEffect(() => {
    if (milestoneDetails) {
      setMilestoneLoader(false);
    }
  }, [milestoneDetails]);

  // Add fallback for when milestoneDetails is not available
  if (!milestoneDetails) {
    return (
      <div style={fallbackStyles.container}>
        <header style={fallbackStyles.header}>
          <div style={fallbackStyles.headerContent}>
            <LeftArrowIcon
              onClick={() => navigate(-1)}
              style={{ cursor: "pointer" }}
            />
            <div style={fallbackStyles.idText}>
              Project Details Not Available
            </div>
          </div>
        </header>
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <p>Milestone details not found. Please go back and try again.</p>
        </div>
      </div>
    );
  }

  // Add loading state for when milestoneDetails is loading
  if (milestoneLoader) {
    return (
      <div style={fallbackStyles.container}>
        <header style={fallbackStyles.header}>
          <div style={fallbackStyles.headerContent}>
            <LeftArrowIcon
              onClick={() => navigate(-1)}
              style={{ cursor: "pointer" }}
            />
            <div style={fallbackStyles.idText}>Loading...</div>
          </div>
        </header>
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  async function fetchSnagDetails() {
    setLoader(true);
    try {
      let params = [];
      if (categoryFilter && categoryFilter.length >= 3)
        params.push(`category=${encodeURIComponent(categoryFilter)}`);
      if (statusFilter) params.push(`status=${statusFilter}`);
      params.push(`page=${page}`);
      params.push(`per_page=3`);
      const queryString = params.length ? `?${params.join("&")}` : "";

      const response = await getApiData(
        `api/admin/inspection/${visitId}/snags${queryString}`,
        "GET",
        {}
      );

      if (
        response.success &&
        isArray(response?.data) &&
        !isEmpty(response?.data)
      ) {
        setDetails(response);
        setTotalPages(Number(response?.total_pages) || 0);
      } else {
        setDetails({ data: [] });
        setTotalPages(0);
      }
    } catch (error) {
      console.log("error:===>>>", error);
      // Set empty data on error to prevent crashes
      setDetails({ data: [] });
      setTotalPages(0);
    } finally {
      setLoader(false);
    }
  }

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  return (
    <>
      <div style={fallbackStyles.container}>
        <header style={fallbackStyles.header}>
          {milestoneLoader ? (
            <center>
              <CircularProgress />
            </center>
          ) : (
            <div style={fallbackStyles.headerContent}>
              <LeftArrowIcon
                onClick={() => navigate(-1)}
                style={{ cursor: "pointer" }}
              />
              <div
                style={{
                  ...fallbackStyles.idText,
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {milestoneDetails?.projectName}
              </div>
            </div>
          )}
        </header>

        <div style={fallbackStyles.subContainer}>
          <div style={{ display: "flex" }}>
            <div style={{ ...fallbackStyles.rowFlex, gap: "15px", flex: "1" }}>
              <Box
                component="img"
                src={
                  milestoneDetails?.milestone_image
                    ? milestoneDetails?.milestone_image?.path
                    : "https://renohome.blob.core.windows.net/reno-cms/e56d3d53-e335-425f-990e-16e6b2bbee1b"
                }
                alt="Project Icon"
                sx={{
                  width: 90,
                  height: 90,
                  borderRadius: "16px",
                  marginRight: 1,
                }}
              />
              <div style={fallbackStyles.columnFlex}>
                <h2 style={fallbackStyles.projectHeading}>
                  {milestoneDetails?.milestoneName}
                </h2>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <Box
                    sx={{
                      width: "15px",
                      height: "15px",
                      borderRadius: "50%",
                      border: "3px solid rgb(229, 229, 229)",
                      backgroundColor:
                        inspectionStatusNewStyleArray.find(
                          (style) => style.status === milestoneDetails?.status
                        )?.backgroundColor || "#FFAD5C",
                    }}
                  />
                  <Box
                    sx={{
                      fontSize: "14px",
                      color: "#0D0D0E",
                    }}
                  >
                    {getStatusDisplayText(milestoneDetails?.status)}
                  </Box>
                </Box>
              </div>
            </div>

            <div style={{ ...fallbackStyles.columnFlex, flex: "1" }}>
              <p style={fallbackStyles.idLabel}>Due Date</p>
              <p style={fallbackStyles.price}>
                {milestoneDetails?.dueDate
                  ? moment(milestoneDetails.dueDate).format("MMM DD, YYYY")
                  : "N/A"}
              </p>
            </div>
          </div>
        </div>

        <div
          style={{
            ...fallbackStyles.overview_section,
            margin: "1vh 2vw 0 2vw",
          }}
        >
          <div
            style={{
              ...fallbackStyles.section_1,
              ...fallbackStyles.columnFlex,
            }}
          >
            <div style={{ ...fallbackStyles.columnFlex }}>
              <h3 style={fallbackStyles.section_sub_heading}>
                {details?.notes || "-"}
              </h3>
            </div>
            <div style={{ width: "50%" }}>
              <Box display="flex" gap={2} alignItems="center" margin="16px 0">
                <CInput
                  label="Category"
                  placeholder="Search category"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && search.length >= 3) {
                      setCategoryFilter(search);
                    }
                  }}
                  inputProps={{ maxLength: 50 }}
                  style={{ flex: 1 }}
                />

                <CAutocomplete
                  label="Status"
                  placeholder="Select status"
                  options={[
                    { label: "Open", value: "OPEN" },
                    {
                      label: "Ready for Inspection",
                      value: "READY_FOR_INSPECTION",
                    },
                    { label: "Resolved", value: "RESOLVED" },
                    { label: "Unresolved", value: "UNRESOLVED" },
                  ]}
                  value={
                    [
                      { label: "Open", value: "OPEN" },
                      {
                        label: "Ready for Inspection",
                        value: "READY_FOR_INSPECTION",
                      },
                      { label: "Resolved", value: "RESOLVED" },
                      { label: "Unresolved", value: "UNRESOLVED" },
                    ].find((option) => option.value === statusFilter) || null
                  }
                  onChange={(e, newValue) => {
                    setStatusFilter(newValue ? newValue.value : "");
                  }}
                  getOptionLabel={(option) => option?.label || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  clearOnEscape
                  style={{ minWidth: 180 }}
                />
              </Box>
            </div>

            <div
              style={{ ...styles.columnFlex, height: "55vh" }}
              className={stylesModule.snagListScroll}
            >
              {loader ? (
                <center>
                  <CircularProgress />
                </center>
              ) : details?.data?.length ? (
                details?.data?.map((snag, idx) => (
                  <SnagList
                    snag={snag}
                    key={idx}
                    onClick={(snag) => {
                      setSelectedSnag(snag);
                      setIsModalOpen(true);
                    }}
                  />
                ))
              ) : (
                <p>No snags found.</p>
              )}
            </div>
            <center
              style={{
                alignSelf: "center",
                width: "100%",
                justifyContent: "center",
                alignItems: "center",
                display: "flex",
              }}
            >
              {totalPages > 1 ? (
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  style={{
                    alignSelf: "center",
                  }}
                  size="large"
                  showFirstButton
                  showLastButton
                />
              ) : (
                <div
                  style={{
                    color: "#666",
                    fontSize: "14px",
                    fontStyle: "italic",
                  }}
                >
                  {totalPages === 0
                    ? "No pages to display"
                    : "Showing all results"}
                </div>
              )}
            </center>
          </div>
        </div>
      </div>
      <SnagDetailsModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        snag={selectedSnag}
      />
    </>
  );
}

export default MilestoneSnagDetails;
